import type { CursorPage, Insight, MemoryItem } from '@mira/clients-shared';
import { useRef, useState } from 'react';
import { createWebApiClient } from '../../api/webSession.js';
import { hrefForRoute, navigateTo } from '../../app/router.js';
import { showToast } from '../../app/toast.js';
import { InsightDetailContent, InsightVisualRow } from '../../ui/domain/index.js';
import { Button, EmptyState, ErrorState, IconGlyph, MiraIcon, SegmentedTabs, Sheet } from '../../ui/primitives/index.js';
import { ConfirmSheet, ListState } from '../../ui/patterns/index.js';
import { useApiQuery } from '../api-state.js';
import { formatDateLabel, page } from '../shared.js';
import { selectedObject } from './selection.js';

export function MemoryScreen(props: {
  insightId?: string | null;
  memoryId?: string | null;
  showInsightDelete?: boolean;
  showInsightSheet?: boolean;
  showMemoryDelete?: boolean;
  tab?: string | null;
}) {
  const tab = memoryTab(props.tab);
  const [search, setSearch] = useState('');
  const memories = useApiQuery<CursorPage<MemoryItem>>({
    preview: false,
    previewData: page([]),
    emptyData: page([]),
    load: () => createWebApiClient().memoryItems({ limit: 30 }),
    isEmpty: (value) => value.items.length === 0,
    deps: [tab],
  });
  const insights = useApiQuery<CursorPage<Insight>>({
    preview: false,
    previewData: page([]),
    emptyData: page([]),
    load: () => createWebApiClient().insights({ limit: 30 }),
    isEmpty: (value) => value.items.length === 0,
    deps: [tab],
  });
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const visibleMemories = filterMemories(memories.data.items, search);
  const memory = selectedObject(memories.data.items, props.memoryId);
  const insight = selectedObject(insights.data.items, props.insightId);

  async function runAction(action: () => Promise<void>, failure: string) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setActionError('');
    try {
      await action();
    } catch {
      setActionError(failure);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  function confirmMemory(memory: MemoryItem) {
    return runAction(async () => {
      await createWebApiClient().confirmMemoryItem(memory.id);
      showToast('Memory confirmed.');
      memories.reload();
    }, 'This memory could not be confirmed right now. Please try again.');
  }

  function deleteMemory(memory: MemoryItem) {
    return runAction(async () => {
      await createWebApiClient().deleteMemoryItem(memory.id);
      showToast('Memory deleted.');
      memories.reload();
      navigateTo('memory', {}, { tab: 'memory' });
    }, 'This memory could not be deleted right now. Please try again.');
  }

  function deleteInsight(insight: Insight) {
    return runAction(async () => {
      await createWebApiClient().deleteInsight(insight.id);
      showToast('Insight deleted.');
      insights.reload();
      navigateTo('memory', {}, { tab: 'insights' });
    }, 'This insight could not be deleted right now. Please try again.');
  }

  function openMemoryDelete(memory: MemoryItem) {
    setActionError('');
    navigateTo('memory', {}, { tab: 'memory', sheet: 'delete-memory', memoryId: memory.id });
  }

  function openInsightDelete(insight: Insight) {
    setActionError('');
    navigateTo('memory', {}, { tab: 'insights', sheet: 'delete-insight', insightId: insight.id });
  }

  function continueInsight(insight: Insight) {
    return runAction(async () => {
      const response = await createWebApiClient().continueInsight(insight.id);
      navigateTo('chat', { conversationId: response.conversationId }, {
        source: 'memory',
        returnTo: `/memory?tab=insights&sheet=insight&insightId=${encodeURIComponent(insight.id)}`,
      });
    }, 'Could not continue from this insight right now. Please try again.');
  }

  return (
    <article className="mira-business-screen mira-memory-screen" data-tab={tab}>
      <header className="mira-memory-header">
        <div className="mira-memory-leaves" aria-hidden="true" />
        <div className="mira-main-brand-row">
          {tab === 'memory' ? <span className="mira-main-brand">Mira</span> : <span />}
          <a className="mira-header-round-icon" href={hrefForRoute('settings')} aria-label="Settings"><MiraIcon name="action.settings" /></a>
        </div>
        <h1>{tab === 'memory' ? 'Memory' : 'Insights'}</h1>
        <p>{tab === 'memory' ? 'Only long-term understanding you confirmed keeps shaping later conversations.' : 'Reminders worth revisiting. They are never read back as long-term memory.'}</p>
      </header>
      <SegmentedTabs className="mira-memory-tabs" value={tab} items={memoryTabs().map(([id, label]) => ({ id, label }))} onChange={(id) => navigateTo('memory', {}, { tab: id })} />
      <label className="mira-memory-search-wrap">
        <span aria-hidden="true"><IconGlyph value="⌕" /></span>
        <input aria-label={tab === 'memory' ? 'Search memories' : 'Search insights'} onChange={(event) => setSearch(event.currentTarget.value)} placeholder={tab === 'memory' ? 'Search memories' : 'Search insights'} type="search" value={search} />
      </label>
      {tab === 'insights' ? (
        <ListState
          state={insights.state}
          emptyTitle="No insights yet"
          emptyBody="When a conversation forms a reminder worth revisiting, it shows up here."
          emptyActionHref={hrefForRoute('today')}
          emptyActionLabel="Back to Today"
          errorTitle={insights.error || 'Insights could not be loaded'}
          errorBody="Insights could not be loaded right now. Please try again later."
          errorActionHref={hrefForRoute('today')}
          errorActionLabel="Back to Today"
        >
          <div className="mira-memory-stack">
            <p className="mira-memory-intro"><span aria-hidden="true"><IconGlyph value="▱" /></span>Revisit, continue, or delete these reminders.</p>
            <div className="mira-insight-visual-list">
              {filterInsights(insights.data.items, search).map((insight) => (
                <InsightVisualRow insight={insight} key={insight.id} onDelete={() => openInsightDelete(insight)} />
              ))}
            </div>
            {search.trim() && filterInsights(insights.data.items, search).length === 0 ? <EmptyState title={`No matches for “${search.trim()}”`} /> : null}
          </div>
          {insight && props.showInsightSheet ? (
            <Sheet scrimClassName="mira-insight-sheet-scrim" title="Insight detail">
              <InsightDetailContent
                closeHref={hrefForRoute('memory', {}, { tab: 'insights' })}
                insight={insight}
                busy={busy}
                onContinue={() => continueInsight(insight)}
                onDelete={() => openInsightDelete(insight)}
              />
            </Sheet>
          ) : null}
        </ListState>
      ) : tab === 'memory' ? (
        <ListState
          state={memories.state}
          emptyTitle="No memories yet"
          emptyBody="Mira only suggests a memory when a conversation forms understanding worth keeping long-term."
          emptyActionHref={hrefForRoute('today')}
          emptyActionLabel="Back to Today"
          errorTitle={memories.error || 'Memories could not be loaded'}
          errorBody="Memories could not be loaded right now. Please try again later."
          errorActionHref={hrefForRoute('today')}
          errorActionLabel="Back to Today"
        >
          <div className="mira-memory-stack">
            <MemorySection
              count={visibleMemories.filter((item) => item.status === 'pending').length}
              icon="◷"
              kicker="Suggested"
              memories={visibleMemories.filter((item) => item.status === 'pending')}
              onConfirm={confirmMemory}
              onDelete={openMemoryDelete}
              confirmBusy={busy}
              deleteBusy={busy}
              subtitle="Mira only uses it after you confirm. Unsure? Just delete it."
              title="Suggested memories"
            />
            <MemorySection
              count={visibleMemories.filter((item) => item.status === 'confirmed').length}
              icon="✓"
              kicker="Confirmed"
              memories={visibleMemories.filter((item) => item.status === 'confirmed')}
              onDelete={openMemoryDelete}
              deleteBusy={busy}
              subtitle="Confirmed items that shape later understanding."
              title="Confirmed"
            />
            {search.trim() && visibleMemories.length === 0 ? <EmptyState title={`No matches for “${search.trim()}”`} /> : null}
          </div>
        </ListState>
      ) : null}
      {actionError ? <ErrorState title="Action failed" body={actionError} /> : null}
      {(props.showMemoryDelete && memories.state === 'ready' && !memory) || ((props.showInsightSheet || props.showInsightDelete) && insights.state === 'ready' && !insight) ? (
        <ErrorState title="Nothing selected" body="Select a specific item from the list. Nothing else gets touched automatically." actionHref={hrefForRoute('memory', {}, { tab })} actionLabel="Back to list" />
      ) : null}
      {props.showMemoryDelete && memory ? (
        <ConfirmSheet
          body="After deletion, Mira will no longer use this as long-term memory."
          busy={busy}
          className="mira-confirm-sheet-memory"
          confirmLabel="Delete"
          error={actionError}
          icon="⌫"
          onCancel={() => navigateTo('memory', {}, { tab: 'memory' })}
          onConfirm={() => deleteMemory(memory)}
          preview={<MemoryDeletePreview memory={memory} />}
          title="Delete this memory?"
        />
      ) : null}
      {props.showInsightDelete && insight ? (
        <ConfirmSheet
          body="This insight leaves the insights list. If later conversations form a similar understanding from new sources, it may return."
          busy={busy}
          confirmLabel="Delete"
          error={actionError}
          icon="⌫"
          onCancel={() => navigateTo('memory', {}, { tab: 'insights' })}
          onConfirm={() => deleteInsight(insight)}
          preview={<InsightDeletePreview insight={insight} />}
          title="Delete this insight?"
        />
      ) : null}
    </article>
  );
}

function MemoryDeletePreview(props: { memory: MemoryItem }) {
  return (
    <>
      <section className="mira-confirm-preview">
        <span aria-hidden="true"><IconGlyph value="♧" /></span>
        <div>
          <h3>{props.memory.title}</h3>
          <p>{props.memory.content}</p>
        </div>
      </section>
      <p className="mira-confirm-footnote">This memory belongs to your account only. You can confirm or delete it.</p>
    </>
  );
}

function InsightDeletePreview(props: { insight: Insight }) {
  return (
    <section className="mira-confirm-preview">
      <span aria-hidden="true"><IconGlyph value="♧" /></span>
      <div>
        <h3>{props.insight.title}</h3>
        <p>{props.insight.content}</p>
      </div>
    </section>
  );
}

type MemoryTab = 'memory' | 'insights';

function memoryTabs(): Array<[MemoryTab, string]> {
  return [['memory', 'Memory'], ['insights', 'Insights']];
}

function memoryTab(value?: string | null): MemoryTab {
  if (value === 'insights') return value;
  return 'memory';
}

function MemorySection(props: {
  confirmBusy?: boolean;
  count: number;
  deleteBusy?: boolean;
  icon: string;
  kicker?: string;
  memories: MemoryItem[];
  onConfirm?: (memory: MemoryItem) => void;
  onDelete: (memory: MemoryItem) => void;
  subtitle: string;
  title: string;
}) {
  return (
    <section className="mira-memory-section">
      <header>
        <span aria-hidden="true"><IconGlyph value={props.icon} /></span>
        <div>
          {props.kicker ? <small>{props.kicker}</small> : null}
          <h2>{props.title}</h2>
        </div>
        <small>{props.count}</small>
      </header>
      <p>{props.subtitle}</p>
      <div className="mira-memory-card-list">
        {props.memories.map((memory) => (
          <article className="mira-memory-visual-card" key={memory.id}>
            <span aria-hidden="true"><IconGlyph value={memoryIcon(memory)} /></span>
            <div>
              <h3>{memory.title}</h3>
              <p>{memory.content}</p>
              <small>Source: conversation understanding · {formatDateLabel(memory.updated_at || memory.created_at)}</small>
            </div>
            <div className="mira-memory-card-actions">
              {memory.status === 'pending' && props.onConfirm ? <Button busy={props.confirmBusy} disabled={props.confirmBusy} onClick={() => props.onConfirm?.(memory)}>{props.confirmBusy ? 'Confirming…' : 'Confirm'}</Button> : null}
              <Button busy={props.deleteBusy} disabled={props.deleteBusy} tone="secondary" onClick={() => props.onDelete(memory)}>{props.deleteBusy ? 'Deleting…' : memory.status === 'pending' ? 'Dismiss' : 'Delete'}</Button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function memoryIcon(memory: MemoryItem): string {
  if (memory.status === 'confirmed') return '✓';
  return '☷';
}

function filterMemories(items: MemoryItem[], search: string): MemoryItem[] {
  const query = search.trim().toLowerCase();
  return query ? items.filter((item) => `${item.title} ${item.content}`.toLowerCase().includes(query)) : items;
}

function filterInsights(items: Insight[], search: string): Insight[] {
  const query = search.trim().toLowerCase();
  return query ? items.filter((item) => `${item.title} ${item.content} ${(item.tags || []).join(' ')}`.toLowerCase().includes(query)) : items;
}
